export type AvailabilityStatus = 'available' | 'soldout' | 'paused';

export interface ComboRequirement {
  readonly selectorKey: string;
  readonly quantity: number;
}

export interface ComboPricingRule {
  readonly ruleId: string;
  readonly enabled: boolean;
  readonly priority: number;
  readonly operationsLabel?: string;
  readonly customerLabel?: string;
  readonly requirements: readonly ComboRequirement[];
  /** Legacy/fixed combo total. Remains authoritative only when componentProductMinor is absent. */
  readonly fixedBaseTotalMinor: number;
  /**
   * Optional Admin-published per-product amount inside this combo.
   * When present, only mapped products are eligible for this rule and the combo base total
   * is derived from the mapped amounts for the consumed units. Standalone product pricing
   * remains separate from meal/combo pricing.
   */
  readonly componentProductMinor?: Readonly<Record<string, number | undefined>>;
}

export interface ModifierForbiddenPair {
  readonly leftOptionId: string;
  readonly rightOptionId: string;
}

export interface ModifierGroupRule {
  readonly ruleId: string;
  readonly enabled: boolean;
  readonly productId: string;
  readonly optionGroupId: string;
  readonly requireSameTierWhenMultiple?: boolean;
  readonly optionTiers?: Readonly<Record<string, string | undefined>>;
  readonly exclusiveOptionIds?: readonly string[];
  readonly forbiddenPairs?: readonly ModifierForbiddenPair[];
  readonly additionalSelectionSurchargeMinor?: number;
}

export const modifierPriceKey = (
  productId: string,
  optionGroupId: string,
  optionId: string,
): string => `${productId}::${optionGroupId}::${optionId}`;

export interface BusinessPolicySnapshot {
  readonly policyVersion: string;
  readonly menuRevision: string;
  readonly pricingRevision: string;
  readonly availabilityRevision: string;
  readonly rulesRevision: string;
  readonly currency: string;
  readonly pricing: {
    readonly productBaseMinor: Readonly<Record<string, number | undefined>>;
    readonly modifierOptionMinor: Readonly<Record<string, number | undefined>>;
  };
  readonly visibility: {
    readonly customer: Readonly<Record<string, boolean | undefined>>;
    readonly operations: Readonly<Record<string, boolean | undefined>>;
  };
  readonly availability: {
    readonly nodes: Readonly<Record<string, AvailabilityStatus | undefined>>;
    readonly productRequirements: Readonly<Record<string, readonly string[] | undefined>>;
  };
  readonly comboRules: readonly ComboPricingRule[];
  readonly modifierRules: readonly ModifierGroupRule[];
}
